import { useEffect, useId, useLayoutEffect, useRef } from "react";
import type {
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from "react";
import { ChevronDown } from "lucide-react";

// 安靜欄位。業主選定的方向:「欄位平常無框、hover 才出底線、focus 才顯框。
// 視覺重量讓給內容本身。」
//
//   平常          專案名稱   2026台灣胸腔暨重症加護醫學會年會
//   hover         專案名稱   2026台灣胸腔暨重症加護醫學會年會
//                           ─────────────────────────────
//   focus         專案名稱  ┌─────────────────────────────┐
//                          │2026台灣胸腔暨重症加護醫學會年會│
//                          └─────────────────────────────┘
//
// 拿掉邊框會順手拿掉四件不能拿的東西,所以這裡逐條寫死:
//
// 1. 焦點環。無框之後,鍵盤使用者若再沒有焦點環就完全不知道自己在哪。
//    焦點環來自 tokens.css 的 `:focus-visible`,這裡只補上 focus-visible 的框線與
//    底色 —— 絕不使用 outline-none(tests/focus-ring.test.ts 會抓)。
// 2. 佔位提示。無框 + 無值 = 看不出這裡可以打字,所以空欄位一定要給 placeholder。
// 3. 唯讀與可編輯的差別。唯讀欄有填色底、沒有 hover 底線、游標不是 I 字,
//    使用者不會對著它打字。
// 4. 對比。安靜不等於淡到看不見:三個 --field-* 代幣都在 tokens-contrast 測試裡。

export interface QuietFieldOption {
  value: string;
  label: string;
}

interface QuietFieldBase {
  /** 可見標籤。一律用 <label htmlFor> 綁定,不靠 placeholder 當標籤。 */
  label: ReactNode;
  /** 標籤下方的說明文字;有錯誤時讓位給錯誤訊息。 */
  hint?: ReactNode;
  /** 錯誤訊息。有值時 aria-invalid 為 true,並用 aria-describedby 連起來。 */
  error?: string | null;
  /** 金額或數量欄:靠右並用表格數字對齊。 */
  numeric?: boolean;
  /**
   * 貼在控制項右邊的單位記號(例如稅率的「%」)。
   *
   * 單位不是裝飾:欄位裡只寫「5」的話,那是 5% 還是 5 元看不出來。所以它同時
   * 做兩件事 —— 畫面上就在欄位旁邊,並且掛進 aria-describedby,螢幕閱讀器
   * 唸完值之後也會唸到單位。
   */
  suffix?: ReactNode;
}

export type QuietFieldProps =
  | (QuietFieldBase & { as?: "input" } & InputHTMLAttributes<HTMLInputElement>)
  | (QuietFieldBase & { as: "textarea" } & TextareaHTMLAttributes<HTMLTextAreaElement>)
  | (QuietFieldBase & { as: "select"; options: QuietFieldOption[] } & SelectHTMLAttributes<HTMLSelectElement>);

/** 元件內部看到的樣子:三種控制項的屬性合成一包,交給 JSX 時各自轉回去。 */
interface QuietFieldInternal extends QuietFieldBase {
  as?: "input" | "textarea" | "select";
  options?: QuietFieldOption[];
  suffix?: ReactNode;
  className?: string;
  id?: string;
  readOnly?: boolean;
  disabled?: boolean;
  required?: boolean;
  [key: string]: unknown;
}

// 三段樣式。平常什麼都沒有,hover 一條線,focus 一個框 —— 順序就是這樣疊上去的。
const CONTROL_BASE =
  "w-full min-w-0 rounded-[var(--radius)] border border-transparent px-2 py-1 text-sm " +
  "text-[var(--text)] placeholder:text-[var(--field-placeholder)] " +
  "transition-colors " +
  // 聚焦才顯框:框線與底色一起回來。焦點環本身由 tokens.css 的 :focus-visible 畫。
  "focus-visible:border-[color:var(--border-strong)] focus-visible:bg-[var(--surface)] ";

const CONTROL_EDITABLE =
  // 平常無框、透明底;hover 只出一條低調底線,告訴使用者這一格可以打字。
  "bg-transparent cursor-text hover:border-b-[color:var(--field-hover)] ";

const CONTROL_READONLY =
  // 唯讀:填色底 + 次要文字色 + 沒有 hover 底線,一眼看得出打不進去。
  "bg-[var(--field-readonly)] text-[var(--text-2)] cursor-default ";

const CONTROL_ERROR =
  // 錯誤不能等 hover 才現形,底線一直在。
  "border-b-[color:var(--danger)] focus-visible:border-[color:var(--danger)] ";

/** 控制項的樣式選項。與 QuietField 自己用的是同一組,只是拆出來給別人用。 */
export interface QuietControlOptions {
  readOnly?: boolean;
  numeric?: boolean;
  error?: boolean;
}

/**
 * 安靜控制項的 class。
 *
 * 明細列是表格,每一格上方沒有標籤欄位可放(欄名在表頭),所以套不進
 * QuietField 那個「標籤 + 控制項」的版型。但樣式必須是同一套 ——
 * 表頭無框、明細列有框的話,同一個畫面上會有兩種欄位語彙。
 * 因此這裡把三段樣式開放出來共用,而不是在明細列另外寫一份。
 */
export function quietControlClass(options: QuietControlOptions = {}): string {
  return (
    CONTROL_BASE +
    (options.readOnly ? CONTROL_READONLY : CONTROL_EDITABLE) +
    (options.error ? CONTROL_ERROR : "") +
    (options.numeric ? "text-right tabular " : "")
  );
}

/** 多行欄位的 class。拉桿關死,手動拉的把手也拿掉 —— 高度是程式管的。 */
export const QUIET_TEXTAREA_CLASS = "overflow-y-hidden resize-none";

/**
 * 讓 textarea 貼合內容高度,並且**永遠不出現垂直拉桿**。
 *
 * 業主原話(2026-09-10):「再報價單內 每一欄位 禁止出現垂直拉桿」。
 * 拉桿會冒出來是因為多行欄位固定幾行高、內容比它長;正解是讓欄位跟著內容長高,
 * 而不是給一根拉桿讓人在一格 24px 的窗口裡捲。
 *
 * 高度既然是程式管的,`overflow-y` 與 `resize` 就一起用**行內樣式**釘死:
 * 外面傳進來的 class(例如報價單表頭那兩欄的 `resize-y`)不會再把拉桿放回來,
 * 使用者手動拉過的高度也不會跟自動算出來的高度打架。
 *
 * **量不到高度時不裁字。** 先歸零再讀 scrollHeight(否則縮短內容時高度只漲不縮);
 * 讀到 0 的時候(jsdom、字體還沒載完、元素還沒進版面)不硬寫一個數字,改成依換行數
 * 給足 rows —— 報價單上看不到的字,PDF 會照印出去,寧可多留一點白。
 */
export function autoGrow(el: HTMLTextAreaElement | null): void {
  if (!el) return;
  el.style.overflowY = "hidden";
  el.style.resize = "none";
  el.style.height = "auto";
  const measured = el.scrollHeight;
  if (measured === 0) {
    el.rows = Math.max(1, String(el.value ?? "").split("\n").length);
    return;
  }

  el.style.height = `${measured}px`;
  // scrollHeight 不含邊框,而 Tailwind 的 box-sizing 是 border-box —— 直接把它寫進
  // height,內容區就少了上下框線那 2px,最後一行的字腳會被切掉(2026-09-10 在瀏覽器裡
  // 量到的實況:scrollHeight 27、clientHeight 25)。拉桿被關掉之後這種缺角是無聲的,
  // 所以用瀏覽器自己算出來的差額補回去,不去解析 CSS 字串。
  const visible = el.clientHeight;
  const shortfall = visible > 0 ? el.scrollHeight - visible : 0;
  if (shortfall > 0) el.style.height = `${measured + shortfall}px`;
}

/**
 * 自動增高的 textarea 用的 ref。
 *
 * **不可以寫成 ``ref={autoGrow}``。** 那是一個模組層級的穩定函式,React 只在
 * 掛載與卸載時呼叫它一次 —— 程式帶入的值(例如就地建議選中一個品項庫項目時,
 * name 與 spec 是一起被寫進去的)完全不會觸發重新量高,欄位就停在掛載時算出的
 * 一行,第二行以後的字使用者永遠看不到。
 *
 * 所以改成依 ``value`` 跑的 layout effect,再加三個會讓「同樣的字佔不同高度」的
 * 時機一起重量:視窗縮放、欄寬改變、網頁字體載入完成。
 */
export function useAutoGrow(value: string) {
  const ref = useRef<HTMLTextAreaElement | null>(null);

  // layout effect:在瀏覽器畫下一格之前就把高度調好,不會先閃一下一行高
  useLayoutEffect(() => {
    autoGrow(ref.current);
  }, [value]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const remeasure = () => autoGrow(ref.current);
    window.addEventListener("resize", remeasure);
    return () => window.removeEventListener("resize", remeasure);
  }, []);

  // 欄寬變了(側邊欄收合、章節換版面、表格欄寬重算),同樣的字會用掉不同的行數,
  // 而先前算出的高度是寫死的 px。只在**寬度**真的變了才重量 —— 高度是我們自己改的,
  // 不擋掉就會變成 ResizeObserver 的無窮迴圈。
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    let lastWidth = el.clientWidth;
    const observer = new ResizeObserver(() => {
      if (el.clientWidth === lastWidth) return;
      lastWidth = el.clientWidth;
      autoGrow(el);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // 網頁字體是非同步載入的,第一次量高很可能是在 fallback 字體上量的,
  // 字體換上去之後同樣的字會佔不同高度 —— 不重量就會差一行,差的那一行就是拉桿。
  useEffect(() => {
    const fonts = document.fonts;
    if (!fonts?.ready) return;
    let cancelled = false;
    void fonts.ready.then(() => {
      if (!cancelled) autoGrow(ref.current);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return ref;
}

export function QuietField(props: QuietFieldProps) {
  const {
    as = "input",
    label,
    hint,
    error = null,
    numeric = false,
    suffix,
    options,
    className = "",
    id,
    readOnly = false,
    required = false,
    ...rest
  } = props as QuietFieldInternal;

  const autoId = useId();
  const fieldId = id ?? autoId;
  // 三種控制項共用同一個元件,hook 一律無條件呼叫;只有 textarea 會真的拿到這個 ref,
  // 其餘兩種的 ref.current 是 null,autoGrow() 什麼都不做。
  const textareaRef = useAutoGrow(String((rest as { value?: unknown }).value ?? ""));
  // 有錯誤時讓錯誤訊息說話,說明文字先退下,描述關係也跟著換。
  const showHint = hint != null && hint !== "" && !error;
  const hintId = showHint ? `${fieldId}-hint` : undefined;
  const errorId = error ? `${fieldId}-error` : undefined;
  const suffixId = suffix != null && suffix !== "" ? `${fieldId}-suffix` : undefined;
  const describedBy = [suffixId, hintId, errorId].filter(Boolean).join(" ") || undefined;

  const controlClass = quietControlClass({ readOnly, numeric, error: error != null && error !== "" }) + className;

  const shared = {
    ...rest,
    id: fieldId,
    required: required || undefined,
    "aria-invalid": error ? true : undefined,
    "aria-describedby": describedBy,
    className: controlClass,
  };

  const control =
    as === "textarea" ? (
      <textarea
        {...(shared as TextareaHTMLAttributes<HTMLTextAreaElement>)}
        ref={textareaRef}
        className={controlClass + " " + QUIET_TEXTAREA_CLASS}
        readOnly={readOnly || undefined}
      />
    ) : as === "select" ? (
      <div className="relative min-w-0">
        <select
          {...(shared as SelectHTMLAttributes<HTMLSelectElement>)}
          className={controlClass + " appearance-none pr-7"}
          disabled={readOnly || undefined}
        >
          {(options ?? []).map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        <ChevronDown
          aria-hidden="true"
          size={14}
          className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-[var(--text-3)]"
        />
      </div>
    ) : (
      <input {...(shared as InputHTMLAttributes<HTMLInputElement>)} readOnly={readOnly || undefined} />
    );

  return (
    <div className="flex min-w-0 items-start gap-2 py-0.5">
      <label
        htmlFor={fieldId}
        className="mt-1 w-[6.5rem] shrink-0 whitespace-nowrap text-[13px] leading-5 text-[var(--text-3)]"
      >
        {label}
        {required ? (
          <span aria-hidden="true" className="ml-0.5 text-[var(--danger)]">
            *
          </span>
        ) : null}
      </label>
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        {suffixId ? (
          <div className="flex min-w-0 items-center gap-1">
            {control}
            <span id={suffixId} className="shrink-0 text-sm text-[var(--text-2)]">
              {suffix}
            </span>
          </div>
        ) : (
          control
        )}
        {showHint ? (
          <span id={hintId} className="text-xs text-[var(--text-3)]">
            {hint}
          </span>
        ) : null}
        {error ? (
          <span id={errorId} className="text-xs text-[var(--danger)]">
            {error}
          </span>
        ) : null}
      </div>
    </div>
  );
}
